import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  OutlinedInput,
  TablePagination,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Edit, Delete, Add, Close } from '@mui/icons-material';
import { apiService } from '@/services/api';
import { useTranslation } from '@/hooks/useTranslation';

interface CustomerGroup {
  id: string;
  name: string;
  createdAt?: string;
  updatedAt?: string;
}

const CustomerGroupsPage: React.FC = () => {
  const { t } = useTranslation();

  const [customerGroups, setCustomerGroups] = useState<CustomerGroup[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [editingCustomerGroup, setEditingCustomerGroup] =
    useState<CustomerGroup | null>(null);
  const [deletingCustomerGroup, setDeletingCustomerGroup] =
    useState<CustomerGroup | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const [newCustomerGroup, setNewCustomerGroup] = useState<{ name: string }>({
    name: '',
  });
  const [saving, setSaving] = useState<boolean>(false);
  const [updating, setUpdating] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);

  useEffect(() => {
    const loadCustomerGroups = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.getCustomerGroups();
        setCustomerGroups(response.groups);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : t('customerGroups.errors.failedToLoad');
        setError(message);
        console.error('Error loading customer groups:', err);
      } finally {
        setLoading(false);
      }
    };

    loadCustomerGroups();
  }, [t]);

  const handleEdit = (id: string) => {
    const group = customerGroups.find(g => g.id === id) || null;
    if (group) {
      setEditingCustomerGroup(group);
      setIsEditModalOpen(true);
    }
  };

  const handleDelete = (id: string) => {
    const group = customerGroups.find(g => g.id === id) || null;
    if (group) {
      setDeletingCustomerGroup(group);
      setIsDeleteModalOpen(true);
    }
  };

  const handleAddCustomerGroup = () => {
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    setNewCustomerGroup({ name: '' });
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingCustomerGroup(null);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeletingCustomerGroup(null);
  };

  const handleSaveCustomerGroup = async () => {
    const name = newCustomerGroup.name.trim();
    if (!name) {
      setSnackbar({
        open: true,
        message: t('customerGroups.errors.nameRequired'),
        severity: 'error',
      });
      return;
    }

    try {
      setSaving(true);
      const group = await apiService.createCustomerGroup({ name });
      setCustomerGroups(prev => [...prev, group]);
      setSnackbar({
        open: true,
        message: t('customerGroups.messages.createdSuccess'),
        severity: 'success',
      });
      handleCloseAddModal();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : t('customerGroups.errors.failedToCreate');
      setSnackbar({
        open: true,
        message,
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCustomerGroup = async () => {
    if (!editingCustomerGroup) return;

    const name = editingCustomerGroup.name.trim();
    if (!name) {
      setSnackbar({
        open: true,
        message: t('customerGroups.errors.nameRequired'),
        severity: 'error',
      });
      return;
    }

    try {
      setUpdating(true);
      const updatedGroup = await apiService.updateCustomerGroup(
        editingCustomerGroup.id,
        { name },
      );
      setCustomerGroups(prev =>
        prev.map(group => (group.id === updatedGroup.id ? updatedGroup : group)),
      );
      setSnackbar({
        open: true,
        message: t('customerGroups.messages.updatedSuccess'),
        severity: 'success',
      });
      handleCloseEditModal();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : t('customerGroups.errors.failedToUpdate');
      setSnackbar({
        open: true,
        message,
        severity: 'error',
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingCustomerGroup) return;

    try {
      setDeleting(true);
      await apiService.deleteCustomerGroup(deletingCustomerGroup.id);
      setCustomerGroups(prev =>
        prev.filter(group => group.id !== deletingCustomerGroup.id),
      );
      setSnackbar({
        open: true,
        message: t('customerGroups.messages.deletedSuccess'),
        severity: 'success',
      });
      handleCloseDeleteModal();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : t('customerGroups.errors.failedToDelete');
      setSnackbar({
        open: true,
        message,
        severity: 'error',
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleNewInputChange =
    (field: keyof typeof newCustomerGroup) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setNewCustomerGroup(prev => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const handleEditInputChange =
    (field: keyof CustomerGroup) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (editingCustomerGroup) {
        const value = event.target.value;
        setEditingCustomerGroup(prev =>
          prev
            ? {
                ...prev,
                [field]: value,
              }
            : null,
        );
      }
    };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedCustomerGroups = customerGroups.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('customerGroups.title')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'end',
              alignItems: 'center',
              mb: 3,
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddCustomerGroup}
              sx={{ minWidth: '180px' }}
              disabled={loading}
            >
              {t('customerGroups.addCustomerGroup')}
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ width: '100%', overflowX: 'auto', minWidth: 0 }}>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <Typography variant="h6">
                            {t('customerGroups.table.name')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="h6">
                            {t('customerGroups.table.createdAt')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="h6" />
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedCustomerGroups.map(group => (
                        <TableRow key={group.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {group.name}
                            </Typography>
                          </TableCell>
                          <TableCell>{group.createdAt}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(group.id)}
                                color="primary"
                              >
                                <Edit />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(group.id)}
                                color="error"
                              >
                                <Delete />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={customerGroups.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={isAddModalOpen}
        onClose={handleCloseAddModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">
              {t('customerGroups.modals.add.title')}
            </Typography>
            <IconButton onClick={handleCloseAddModal} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel required>
                {t('customerGroups.modals.add.name')}
              </InputLabel>
              <OutlinedInput
                value={newCustomerGroup.name}
                onChange={handleNewInputChange('name')}
                label={t('customerGroups.modals.add.name')}
                required
              />
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseAddModal} color="secondary">
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSaveCustomerGroup}
            variant="contained"
            color="primary"
            disabled={!newCustomerGroup.name.trim() || saving}
          >
            {saving ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t('customerGroups.modals.add.save')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">
              {t('customerGroups.modals.edit.title')}
            </Typography>
            <IconButton onClick={handleCloseEditModal} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel required>
                {t('customerGroups.modals.edit.name')}
              </InputLabel>
              <OutlinedInput
                value={editingCustomerGroup?.name ?? ''}
                onChange={handleEditInputChange('name')}
                label={t('customerGroups.modals.edit.name')}
                required
              />
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseEditModal} color="secondary">
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleUpdateCustomerGroup}
            variant="contained"
            color="primary"
            disabled={!editingCustomerGroup?.name.trim() || updating}
          >
            {updating ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t('customerGroups.modals.edit.update')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">
              {t('customerGroups.modals.delete.title')}
            </Typography>
            <IconButton onClick={handleCloseDeleteModal} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Typography>
            {t('customerGroups.modals.delete.confirm')}{' '}
            {deletingCustomerGroup?.name}?{' '}
            {t('customerGroups.modals.delete.warning')}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseDeleteModal} color="secondary">
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={deleting}
          >
            {deleting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t('customerGroups.modals.delete.delete')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          variant="filled"
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerGroupsPage;

